import { loadBaseCSS } from '../shared/button-utils.js';
import {
  renderTrashIcon,
  renderVisibilityIcon
} from '../content-blocks/qds-content-icons.js';
import {
  ensureDashboardBounds,
  escapeHtml,
  getContentBlockDisplayName,
  getContentRendererStyles,
  mountContentBlock,
  normalizeContentDocument,
  resolveRegistry
} from '../content-blocks/qds-content-blocks.js';
import type { QdsContentBlockInstance, QdsContentDocument, QdsContentMode } from '../../types/content.js';
import type { QdsBlockDefinition, QdsBlockRegistry, QdsRenderContext } from '../../types/blocks.js';

loadBaseCSS();

type FrameAction =
  | 'select-block'
  | 'duplicate-block'
  | 'remove-block'
  | 'toggle-visibility'
  | 'move-up'
  | 'move-down'
  | 'move-left'
  | 'move-right'
  | 'move-top'
  | 'move-bottom'
  | 'wider'
  | 'narrower'
  | 'taller'
  | 'shorter';

/**
 * @element qds-content-block-frame
 *
 * The editing chrome that wraps a single content block on the canvas, providing a toolbar, selection surface, keyboard controls, pointer-based drag reordering, and dashboard resize/move actions.
 *
 * @attr {string} document-mode - The document mode governing available controls: `page` or `dashboard`
 * @attr {boolean} selected - When present, marks the frame as selected
 * @attr {boolean} readonly - When present, disables editing controls on the frame
 *
 * @property {QdsContentBlockInstance | null} block - The block instance rendered inside the frame
 * @property {QdsBlockDefinition | undefined} definition - The resolved definition for the block
 * @property {QdsContentDocument | null} contentDocument - The parent content document
 * @property {QdsContentMode} documentMode - The document mode governing available controls
 * @property {QdsBlockRegistry | null} registry - The block registry used to resolve block definitions
 * @property {QdsRenderContext | undefined} context - Host-supplied render context (registry, capabilities)
 * @property {boolean} selected - Whether the frame is selected
 * @property {boolean} readonly - Whether editing controls are disabled
 *
 * @fires block-select - Fired when the frame is activated to select its block
 * @fires block-duplicate - Fired when the block's duplicate action is triggered
 * @fires block-remove - Fired when the block's remove action is triggered
 * @fires block-visibility-toggle - Fired when the block's visibility is toggled
 * @fires block-reorder - Fired when the block is reordered in page mode, carrying a direction
 * @fires block-layout-change - Fired when the block's dashboard layout changes (move/resize)
 * @fires block-pointer-drag-start - Fired when a pointer-based drag of the block begins
 * @fires block-pointer-drag-move - Fired as the pointer moves during a block drag
 * @fires block-pointer-drag-end - Fired when a pointer-based block drag ends
 */
export class QdsContentBlockFrame extends HTMLElement {
  private _block: QdsContentBlockInstance | null = null;
  private _definition?: QdsBlockDefinition;
  private _document: QdsContentDocument | null = null;
  private _documentMode: QdsContentMode = 'page';
  private _registry: QdsBlockRegistry | null = null;
  private _context?: QdsRenderContext;
  private _selected = false;
  private _readonly = false;
  private _pointerSession: {
    pointerId: number;
    startX: number;
    startY: number;
    dragging: boolean;
    handle: HTMLElement | null;
  } | null = null;

  static get observedAttributes(): string[] {
    return ['document-mode', 'selected', 'readonly'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    if (!this.isConnected || !this.shadowRoot) {
      return;
    }

    this.shadowRoot.addEventListener('click', this._handleClick);
    this.shadowRoot.addEventListener('pointerdown', this._handlePointerDown as EventListener);
    this.shadowRoot.addEventListener('keydown', this._handleKeyDown as EventListener);
    this.render();
  }

  disconnectedCallback(): void {
    this.shadowRoot?.removeEventListener('click', this._handleClick);
    this.shadowRoot?.removeEventListener('pointerdown', this._handlePointerDown as EventListener);
    this.shadowRoot?.removeEventListener('keydown', this._handleKeyDown as EventListener);
    this._teardownPointerSession();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return;
    }

    if (name === 'document-mode') {
      this._documentMode = newValue === 'dashboard' ? 'dashboard' : 'page';
    }

    if (name === 'selected') {
      this._selected = newValue !== null;
    }

    if (name === 'readonly') {
      this._readonly = newValue !== null;
    }

    this.render();
  }

  get block(): QdsContentBlockInstance | null {
    return this._block;
  }

  set block(value: QdsContentBlockInstance | null) {
    this._block = value;
    this.render();
  }

  get definition(): QdsBlockDefinition | undefined {
    return this._definition;
  }

  set definition(value: QdsBlockDefinition | undefined) {
    this._definition = value;
    this.render();
  }

  get contentDocument(): QdsContentDocument | null {
    return this._document;
  }

  set contentDocument(value: QdsContentDocument | null) {
    this._document = value;
    this.render();
  }

  get documentMode(): QdsContentMode {
    return this._documentMode;
  }

  set documentMode(value: QdsContentMode) {
    this._documentMode = value === 'dashboard' ? 'dashboard' : 'page';
    this.setAttribute('document-mode', this._documentMode);
  }

  get registry(): QdsBlockRegistry | null {
    return this._registry;
  }

  set registry(value: QdsBlockRegistry | null) {
    this._registry = value;
    this.render();
  }

  get context(): QdsRenderContext | undefined {
    return this._context;
  }

  set context(value: QdsRenderContext | undefined) {
    this._context = value;
    this.render();
  }

  get selected(): boolean {
    return this._selected;
  }

  set selected(value: boolean) {
    this._selected = Boolean(value);
    if (this._selected) {
      this.setAttribute('selected', '');
    } else {
      this.removeAttribute('selected');
    }
  }

  get readonly(): boolean {
    return this._readonly;
  }

  set readonly(value: boolean) {
    this._readonly = Boolean(value);
    if (this._readonly) {
      this.setAttribute('readonly', '');
    } else {
      this.removeAttribute('readonly');
    }
  }

  private _dispatch(name: string, detail: Record<string, unknown>): void {
    this.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      composed: true,
      detail
    }));
  }

  private _dispatchSelection(): void {
    if (!this._block) {
      return;
    }

    this._dispatch('block-select', { blockId: this._block.id });
  }

  private _emitLayoutChange(layout: QdsContentBlockInstance['layout']): void {
    if (!this._block || !layout) {
      return;
    }

    this._dispatch('block-layout-change', {
      blockId: this._block.id,
      layout
    });
  }

  private _nextDashboardLayout(action: FrameAction): QdsContentBlockInstance['layout'] | undefined {
    if (!this._block) {
      return undefined;
    }

    const documentModel = normalizeContentDocument(this._document, this._registry);
    const columns = documentModel.layout?.columns || 12;
    const layout = ensureDashboardBounds({
      ...(this._block.layout || {})
    }, columns);

    switch (action) {
      case 'move-left':
        layout.x = Math.max(0, (layout.x || 0) - 1);
        break;
      case 'move-right':
        layout.x = Math.min(columns - Math.max(1, layout.w || 1), (layout.x || 0) + 1);
        break;
      case 'move-top':
        layout.y = Math.max(0, (layout.y || 0) - 1);
        break;
      case 'move-bottom':
        layout.y = (layout.y || 0) + 1;
        break;
      case 'wider':
        layout.w = (layout.w || 1) + 1;
        break;
      case 'narrower':
        layout.w = Math.max(1, (layout.w || 1) - 1);
        break;
      case 'taller':
        layout.h = (layout.h || 1) + 1;
        break;
      case 'shorter':
        layout.h = Math.max(1, (layout.h || 1) - 1);
        break;
      default:
        break;
    }

    return ensureDashboardBounds(layout, columns);
  }

  private _handleClick = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('qds-inline-text-editor, qds-inline-rich-text, [data-frame-ignore-select="true"]')) {
      return;
    }
    const actionElement = target?.closest<HTMLElement>('[data-action]');

    if (!actionElement || !this._block) {
      return;
    }

    const action = actionElement.dataset.action as FrameAction;

    if (action === 'select-block') {
      this._dispatchSelection();
      return;
    }

    if (this._readonly) {
      return;
    }

    switch (action) {
      case 'duplicate-block':
        this._dispatch('block-duplicate', { blockId: this._block.id });
        break;
      case 'remove-block':
        this._dispatch('block-remove', { blockId: this._block.id });
        break;
      case 'toggle-visibility':
        this._dispatch('block-visibility-toggle', { blockId: this._block.id });
        break;
      case 'move-up':
        this._dispatch('block-reorder', { blockId: this._block.id, direction: 'up' });
        break;
      case 'move-down':
        this._dispatch('block-reorder', { blockId: this._block.id, direction: 'down' });
        break;
      case 'move-left':
      case 'move-right':
      case 'move-top':
      case 'move-bottom':
      case 'wider':
      case 'narrower':
      case 'taller':
      case 'shorter': {
        const layout = this._nextDashboardLayout(action);
        this._emitLayoutChange(layout);
        break;
      }
      default:
        break;
    }
  };

  private _handleKeyDown = (event: KeyboardEvent): void => {
    if (!this._block) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (target?.closest('qds-inline-text-editor, qds-inline-rich-text, [data-frame-ignore-select="true"]')) {
      return;
    }
    const tagName = target?.tagName.toLowerCase();
    if (tagName === 'input' || tagName === 'textarea' || tagName === 'select' || target?.isContentEditable) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this._dispatchSelection();
      return;
    }

    if (this._readonly) {
      return;
    }

    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      this._dispatch('block-remove', { blockId: this._block.id });
      return;
    }

    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'd') {
      event.preventDefault();
      this._dispatch('block-duplicate', { blockId: this._block.id });
      return;
    }

    if (this._documentMode === 'page' && event.altKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
      event.preventDefault();
      this._dispatch('block-reorder', {
        blockId: this._block.id,
        direction: event.key === 'ArrowUp' ? 'up' : 'down'
      });
      return;
    }

    if (this._documentMode === 'dashboard' && event.key.startsWith('Arrow')) {
      event.preventDefault();
      const actionMap: Record<string, FrameAction> = event.shiftKey
        ? {
            ArrowLeft: 'narrower',
            ArrowRight: 'wider',
            ArrowUp: 'shorter',
            ArrowDown: 'taller'
          }
        : {
            ArrowLeft: 'move-left',
            ArrowRight: 'move-right',
            ArrowUp: 'move-top',
            ArrowDown: 'move-bottom'
          };
      const action = actionMap[event.key];
      if (action) {
        const layout = this._nextDashboardLayout(action);
        this._emitLayoutChange(layout);
      }
    }
  };

  private _teardownPointerSession(): void {
    window.removeEventListener('pointermove', this._handleWindowPointerMove as EventListener);
    window.removeEventListener('pointerup', this._handleWindowPointerUp as EventListener);
    window.removeEventListener('pointercancel', this._handleWindowPointerUp as EventListener);
    document.body.style.removeProperty('user-select');
    document.body.style.removeProperty('-webkit-user-select');
    if (this._pointerSession?.handle?.hasPointerCapture(this._pointerSession.pointerId)) {
      this._pointerSession.handle.releasePointerCapture(this._pointerSession.pointerId);
    }
    this._pointerSession = null;
    this.shadowRoot?.querySelector('.frame--dragging')?.classList.remove('frame--dragging');
  }

  private _handlePointerDown = (event: PointerEvent): void => {
    if (
      this._readonly ||
      this._documentMode !== 'page' ||
      !this._block ||
      event.button !== 0
    ) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (target?.closest('.frame__action')) {
      return;
    }

    const handle = target?.closest<HTMLElement>('[data-drag-handle]');
    if (!handle) {
      return;
    }

    event.preventDefault();

    if (typeof handle.setPointerCapture === 'function') {
      handle.setPointerCapture(event.pointerId);
    }

    this._pointerSession = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      dragging: false,
      handle
    };

    window.addEventListener('pointermove', this._handleWindowPointerMove as EventListener);
    window.addEventListener('pointerup', this._handleWindowPointerUp as EventListener);
    window.addEventListener('pointercancel', this._handleWindowPointerUp as EventListener);
  };

  private _handleWindowPointerMove = (event: PointerEvent): void => {
    if (!this._pointerSession || !this._block || event.pointerId !== this._pointerSession.pointerId) {
      return;
    }

    const distance = Math.hypot(
      event.clientX - this._pointerSession.startX,
      event.clientY - this._pointerSession.startY
    );

    if (!this._pointerSession.dragging && distance < 6) {
      return;
    }

    if (!this._pointerSession.dragging) {
      this._pointerSession.dragging = true;
      document.body.style.userSelect = 'none';
      document.body.style.setProperty('-webkit-user-select', 'none');
      this.shadowRoot?.querySelector('.frame')?.classList.add('frame--dragging');
      this._dispatch('block-pointer-drag-start', {
        blockId: this._block.id,
        startX: this._pointerSession.startX,
        startY: this._pointerSession.startY
      });
    }

    event.preventDefault();
    this._dispatch('block-pointer-drag-move', {
      blockId: this._block.id,
      clientX: event.clientX,
      clientY: event.clientY
    });
  };

  private _handleWindowPointerUp = (event: PointerEvent): void => {
    if (!this._pointerSession || !this._block || event.pointerId !== this._pointerSession.pointerId) {
      return;
    }

    if (this._pointerSession.dragging) {
      event.preventDefault();
      this._dispatch('block-pointer-drag-end', {
        blockId: this._block.id,
        clientX: event.clientX,
        clientY: event.clientY
      });
    }

    this._teardownPointerSession();
  };

  render(): void {
    if (!this.shadowRoot) {
      return;
    }

    if (!this._block) {
      this.shadowRoot.innerHTML = '';
      return;
    }

    const registry = resolveRegistry(this._registry || this._context?.blockRegistry || null);
    const documentModel = normalizeContentDocument(this._document, registry);
    const block = this._block;
    const definition = this._definition || registry.get(block.type);
    const label = getContentBlockDisplayName(block, registry);
    const hasNestedContent = this.hasAttribute('has-nested-content');
    const layoutStatus = this._documentMode === 'dashboard'
      ? `x ${block.layout?.x || 0} · y ${block.layout?.y || 0} · w ${block.layout?.w || 1} · h ${block.layout?.h || 1}`
      : '';

    this.shadowRoot.innerHTML = `
      <style>
        ${getContentRendererStyles()}

        :host {
          display: block;
          min-height: 44px;
          color: var(--theme-text-primary, #333333);
          font-family: var(--font-family-primary, 'IBM Plex Sans', sans-serif);
        }

        .frame {
          display: grid;
          gap: 0;
          height: 100%;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          border-radius: 0;
          background: var(--theme-background-primary, #ffffff);
          box-shadow: var(--shadow-light, 0 1px 3px rgba(32, 33, 36, 0.1));
          overflow: hidden;
        }

        .frame--draggable {
          cursor: default;
        }

        .frame--dragging {
          opacity: 0.55;
        }

        .frame--draggable .frame__toolbar {
          cursor: grab;
        }

        .frame--dragging .frame__toolbar {
          cursor: grabbing;
        }

        .frame:focus-within {
          /* The selected-frame border already communicates editor focus.
             Avoid adding a second blue outline around inline text editing. */
          outline: none;
        }

        .frame__toolbar {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: center;
          gap: 12px;
          padding: 8px 10px;
          border-bottom: 1px solid var(--theme-border-color, #e0e0e0);
          background: var(--theme-background-secondary, #f8f8f8);
          user-select: none;
          -webkit-user-select: none;
        }

        .frame__toolbar-main {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
          min-width: 0;
        }

        .frame__label {
          margin: 0;
          font-size: 12px;
          font-weight: 600;
          line-height: 1.2;
        }

        .frame__type {
          display: inline-flex;
          align-items: center;
          min-height: 20px;
          padding: 2px 6px;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          border-radius: 0;
          background: var(--theme-background-primary, #ffffff);
          color: var(--theme-text-secondary, #5f6368);
          font-size: 11px;
          line-height: 1;
        }

        .frame__status {
          display: inline-flex;
          align-items: center;
          min-height: 20px;
          padding: 2px 6px;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          border-radius: 0;
          background: var(--theme-background-primary, #ffffff);
          color: var(--theme-text-secondary, #5f6368);
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.04em;
        }

        .frame__toolbar-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 6px;
          cursor: default;
        }

        .frame__action {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          min-height: 24px;
          padding: 0;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          border-radius: 0;
          background: var(--theme-background-primary, #ffffff);
          color: inherit;
          cursor: ${this._readonly ? 'default' : 'pointer'};
        }

        .frame__action svg {
          width: 13px;
          height: 13px;
        }

        .frame__surface {
          display: block;
          width: 100%;
          min-height: 36px;
          padding: 14px;
          border: none;
          border-radius: 0;
          background: transparent;
          text-align: left;
          cursor: pointer;
        }

        .frame__surface:focus-visible,
        .frame__action:focus-visible {
          outline: 2px solid var(--input-focus-border, #4285f4);
          outline-offset: 2px;
        }

        .frame__preview {
          display: grid;
          gap: 12px;
          min-height: 40px;
        }

        .frame__children {
          display: ${hasNestedContent ? 'block' : 'none'};
        }

        .frame__children-slot::slotted(*) {
          display: block;
        }

        @media (max-width: 720px) {
          .frame__toolbar {
            grid-template-columns: minmax(0, 1fr);
          }

          .frame__toolbar-actions {
            justify-content: flex-start;
          }
        }
      </style>
      <article
        class="frame ${this._documentMode === 'page' && !this._readonly ? 'frame--draggable' : ''}"
        data-block-id="${block.id}"
        data-drag-block-id="${this._documentMode === 'page' ? block.id : ''}"
        draggable="false"
      >
        <div class="frame__toolbar" data-drag-handle="true" aria-label="Drag block">
          <div class="frame__toolbar-main">
            <p class="frame__label">${escapeHtml(label)}</p>
            <span class="frame__type">${escapeHtml(definition?.label || block.type)}</span>
            ${layoutStatus ? `<span class="frame__status">${layoutStatus}</span>` : ''}
          </div>
          <div class="frame__toolbar-actions">
            <button
              type="button"
              class="frame__action"
              data-action="toggle-visibility"
              aria-label="${block.visibility?.hidden ? 'Show block' : 'Hide block'}"
              title="${block.visibility?.hidden ? 'Show block' : 'Hide block'}"
              ${this._readonly ? 'disabled' : ''}
            >
              ${renderVisibilityIcon(Boolean(block.visibility?.hidden))}
            </button>
            <button
              type="button"
              class="frame__action"
              data-action="remove-block"
              aria-label="Delete block"
              title="Delete block"
              ${this._readonly ? 'disabled' : ''}
            >
              ${renderTrashIcon()}
            </button>
          </div>
        </div>
        <div
          class="frame__surface"
          data-action="select-block"
          role="button"
          tabindex="0"
          aria-pressed="${this._selected}"
        >
          <div class="frame__preview" id="frame-preview"></div>
        </div>
        <div class="frame__children">
          <slot class="frame__children-slot" name="nested-content"></slot>
        </div>
      </article>
    `;

    const mountPoint = this.shadowRoot.getElementById('frame-preview');
    if (mountPoint) {
      mountContentBlock(mountPoint, {
        block,
        contentDocument: documentModel,
        registry,
        context: this._context || { blockRegistry: registry },
        renderMode: 'design',
        readonly: this._readonly,
        selected: this._selected
      });
    }
  }
}

if (!customElements.get('qds-content-block-frame')) {
  customElements.define('qds-content-block-frame', QdsContentBlockFrame);
}

export default QdsContentBlockFrame;
